import React, {useEffect, useRef, useState} from "react";
import {isArrayNullOrEmpty} from "../../../Utilities/Types/arrayUtilities";
import {PageTitle} from "../../../Components/Landmarks";
import {GridCell, gridConstants, GridContainer} from "../../../Components/Grid";
import {Button, ButtonLinkStyle} from "../../../Components/Inputs";
import {StudentApprovalView} from "./StudentApprovalView";
import {isNullOrUndefined} from "../../../Utilities/commonUtilities";
import {studentApprovalApi} from "./studentApprovalApi";
import {studentCensusApi} from "../studentCensusApi";
import {studentModel} from "./studentApprovalFactory";
import {locations} from "../../../Utilities/Location";
import {STUDENT_APPROVAL_STATUS_FOR_FIMC_ADMIN_SEARCH, STUDENT_APPROVAL_STATUS_FOR_SEARCH} from "../studentConstants";
import {StudentApprovalSearchFormView} from "./StudentApprovalSearchFormView";
import {isFimcAdmin} from "../../../Utilities/LocalStorage/storageUtilities";
import {LayoutFullWidth} from "../../../Components/Layout";
import {StudentApprovalCensusView} from "./StudentApprovalCensusView";

import {studentApi} from "@containers/Student/studentApi";

interface StudentApprovalContainerProps {
    districts: Record<string, any>[];
    handleApiCall: (...args: unknown[]) => void;
    handleSetStudentSearchCriteria: (criteria: Record<string, unknown>) => void;
    isLoading: boolean;
    redirect: (path: string) => void;
    studentSearchCriteria: Record<string, any>;
    userId: string;
}

export const StudentApprovalContainer: React.FC<StudentApprovalContainerProps> = ({
                                             districts,
                                             handleApiCall,
                                             handleSetStudentSearchCriteria,
                                             isLoading,
                                             redirect,
                                             studentSearchCriteria,
                                             userId
                                         }) => {

    const [students, setStudents] = useState<Record<string, unknown>[]>([]);
    const totalRecords = useRef(0);
    const [currentCensusStatus, setCurrentCensusStatus] = useState<string | null>(null);
    const [organizations, setOrganizations] = useState<string[] | null>(null);
    const noResults = isArrayNullOrEmpty(students);
    const linkSpacer = <>&nbsp; | &nbsp;</>;

    const buttonSharedSettings = {
        medium_columns: gridConstants.column.THREE,
        small_columns: gridConstants.column.SIX,
    };

    const handleAddStudentClick = () => {
        redirect(locations.REGISTER_STUDENT.path);
    };

    const handleExportStudentsClick = () => {
        if (noResults) {
            alert("Search criteria has no results. Adjust your search and try again.")
        } else {
            handleApiCall(() => studentCensusApi.exportStudentsReport(studentSearchCriteria));
        }
    };

    const handleEndCensus = () => {
        handleApiCall(() => studentCensusApi.setManualCensusEndDate(), getCurrentCensusStatus);
    };

    const handleAdminReviewCensus = () => {
        handleApiCall(() => studentCensusApi.setManualCensusFimcAdminReviewDate(), getCurrentCensusStatus);
    };

    const handleOpenCensus = () => {
        handleApiCall(() => studentCensusApi.prepareForOpenCensus(), () => {
            loadStudents();
            getCurrentCensusStatus();
        });
    };

    const getCurrentCensusStatus = () => {
        handleApiCall(() => studentCensusApi.getCurrentCensusStatus(), (result: { toString: () => string }) => setCurrentCensusStatus(result.toString()));
    }

    const loadStudentsResults = (result: { data: Record<string, unknown>[]; totalRecords: number }) => {
        setStudents(result.data.map(studentModel));
        totalRecords.current = result.totalRecords;
    };

    const loadStudents = () => {
        handleApiCall(() => studentApprovalApi.getStudentApprovalRequests({
            ...studentSearchCriteria,
            userId
        }), loadStudentsResults);
    };

    const getOrganizations = () => {
        handleApiCall(() => studentApi.getOrganizations(), (result: string[]) => setOrganizations(result));
    };

    useEffect(() => {
        if (isNullOrUndefined(districts) || districts.length === 0) return;

        getCurrentCensusStatus();

        if(!organizations)
         getOrganizations();

        if (studentSearchCriteria.loadSearchResults) {
            if (!isFimcAdmin() && studentSearchCriteria.studentApprovalStatus === STUDENT_APPROVAL_STATUS_FOR_FIMC_ADMIN_SEARCH.Deleted.id.toString()) {
                handleSetStudentSearchCriteria({
                    ...studentSearchCriteria,
                    studentApprovalStatus: STUDENT_APPROVAL_STATUS_FOR_SEARCH.PendingNeedInfo.id
                });
            }
            loadStudents();
        }

        // eslint-disable-next-line
    }, [districts, studentSearchCriteria]);

    if (!studentSearchCriteria) return null;

    return (
        <>
            <PageTitle h1={`Student Management`}/>

            <div className={`searchBox`}>
                <div className={`searchBox__title`}>
                    <span>Search for Students</span>
                    <div className="searchBox__actions">
                        <Button
                            className={`searchBox__addStudents`}
                            isCollapsed
                            isPrimary
                            label={`Add Student`}
                            name="btnAddStudent"
                            onClick={handleAddStudentClick}
                        />
                    </div>
                </div>

                <StudentApprovalSearchFormView
                    buttonSharedSettings={buttonSharedSettings}
                    districts={districts}
                    handleSetStudentSearchCriteria={handleSetStudentSearchCriteria}
                    isLoading={isLoading}
                    organizations={organizations ?? []}
                    studentSearchCriteria={studentSearchCriteria}
                />
            </div>

            <StudentApprovalCensusView
                buttonSharedSettings={buttonSharedSettings}
                currentCensusStatus={currentCensusStatus ?? undefined}
                handleAdminReviewCensus={handleAdminReviewCensus}
                handleEndCensus={handleEndCensus}
                handleOpenCensus={handleOpenCensus}/>

            <LayoutFullWidth className="catalog">
                <header>
                    <h2>Search Results</h2>
                </header>

                <GridContainer isReverseOrder hasMarginX hasMarginY className={`text-right`}>
                    <GridCell>
                        <ButtonLinkStyle
                            isCollapsed
                            label={`Export Students`}
                            name="btnExportStudents"
                            onClick={handleExportStudentsClick}
                        />
                        {linkSpacer}
                        <ButtonLinkStyle
                            isCollapsed
                            label={`Add Student`}
                            name="btnAddStudent"
                            onClick={handleAddStudentClick}
                        />
                    </GridCell>
                </GridContainer>

                <StudentApprovalView
                    handleSetStudentSearchCriteria={handleSetStudentSearchCriteria}
                    redirect={redirect}
                    students={students}
                    studentSearchCriteria={studentSearchCriteria}
                    totalRecords={totalRecords.current}
                />
            </LayoutFullWidth>
        </>
    );
};